import { useState, useEffect } from "react";
import {
  AddCircleOutlineSharp,
  RemoveCircleOutlineSharp,
} from "@mui/icons-material";
import {
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { ArticleIcon } from "../../../../../../../components/icons/ArticleIcon";
import { RootState } from "../../../../../../../store/Index";
import { BookIcon } from "../../../../../../../components/icons/BookIcon";
import CheckoutModal from "./components/CheckoutModal";

function AdminTab() {
  const [selectedServices, setSelectedServices] = useState({
    videoDownload: false,
    memoryBook: false,
  });

  const [total, setTotal] = useState(0);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const handleServiceToggle = (serviceType: "videoDownload" | "memoryBook") => {
    setSelectedServices((prev) => ({
      ...prev,
      [serviceType]: !prev[serviceType],
    }));
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    const newTotal =
      (selectedServices.videoDownload ? 25 : 0) +
      (selectedServices.memoryBook ? 5 : 0);
    setTotal(newTotal);
  }, [selectedServices]);

  const handleCheckout = () => {
    setCheckoutOpen(true);
  };

  const handleCloseCheckout = () => {
    setCheckoutOpen(false);
  };

  return (
    <Box>
      <Typography variant="h5" component="h5" sx={{ mb: 3 }}>
        Additional Services
      </Typography>

      <Grid container spacing={{ xs: 3, md: 4 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Video Download Card */}
          <Card elevation={0} sx={{ borderRadius: 2, mb: { xs: 2, md: 3 } }}>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <IconButton
                sx={{
                  backgroundColor: "grey.300",
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  flexShrink: 0,
                  p: 0,
                  mr: { xs: 0, sm: 2 },
                  mb: { xs: 0, sm: 0 },
                }}
              >
                <ArticleIcon />
              </IconButton>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ mb: 0.1, fontWeight: "bold", fontSize: "12" }}
                >
                  Link to download videos
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                  }}
                >
                  <Typography sx={{ flex: 1, mr: { sm: 4 }, fontSize: "12px" }}>
                    Get a private link to download and keep the full memorial
                    video in HD. Save it, share it with family and cherish the
                    moments forever.
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: { xs: "100%", sm: "auto" },
                      justifyContent: { xs: "space-between", sm: "flex-start" },
                      mt: { xs: 1, sm: 0 },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>USD $25.00</Typography>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: reduxTheme.primary_colors[400],
                        textTransform: "none",
                        minWidth: "120px",
                      }}
                      onClick={() => handleServiceToggle("videoDownload")}
                    >
                      {selectedServices.videoDownload ? (
                        <RemoveCircleOutlineSharp
                          sx={{ fontSize: "medium", mr: 0.5 }}
                        />
                      ) : (
                        <AddCircleOutlineSharp
                          sx={{ fontSize: "medium", mr: 0.5 }}
                        />
                      )}
                      {selectedServices.videoDownload ? "Remove" : "Add"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Memory Book Card */}
          <Card elevation={0} sx={{ borderRadius: 2 }}>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
              }}
            >
              <IconButton
                sx={{
                  backgroundColor: "grey.300",
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  flexShrink: 0,
                  p: 0,
                  mr: { xs: 0, sm: 2 },
                  mb: { xs: 0, sm: 0 },
                }}
              >
                <BookIcon sx={{ fontSize: 60 }} />
              </IconButton>

              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{ mb: 0.1, fontWeight: "bold", fontSize: "12" }}
                >
                  Memory Book
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                  }}
                >
                  <Typography sx={{ flex: 1, mr: { sm: 4 }, fontSize: "12px" }}>
                    A beautifully crafted tribute that gathers all shared
                    memories both online and in person into one keepsake. Filled
                    with photos, messages and heartfelt moments, it preserves
                    your loved one's story for generations.
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: { xs: "100%", sm: "auto" },
                      justifyContent: { xs: "space-between", sm: "flex-start" },
                      mt: { xs: 1, sm: 0 },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>USD $5.00</Typography>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: reduxTheme.primary_colors[400],
                        textTransform: "none",
                        minWidth: "120px",
                      }}
                      onClick={() => handleServiceToggle("memoryBook")}
                    >
                      {selectedServices.memoryBook ? (
                        <RemoveCircleOutlineSharp
                          sx={{ fontSize: "medium", mr: 0.5 }}
                        />
                      ) : (
                        <AddCircleOutlineSharp
                          sx={{ fontSize: "medium", mr: 0.5 }}
                        />
                      )}
                      {selectedServices.memoryBook ? "Remove" : "Add"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              height: { md: "100%" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "12" }}>
                  Quote Details
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {selectedServices.videoDownload && (
                    <Typography
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "12px",
                        mb: 1,
                      }}
                    >
                      <span>Link to download video</span>
                      <span>USD $25.00</span>
                    </Typography>
                  )}
                  {selectedServices.memoryBook && (
                    <Typography
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "12px",
                      }}
                    >
                      <span>Memory Book</span>
                      <span>USD $5.00</span>
                    </Typography>
                  )}
                  {!selectedServices.videoDownload &&
                    !selectedServices.memoryBook && (
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "text.secondary",
                          textAlign: "center",
                          py: 2,
                          fontStyle: "italic",
                        }}
                      >
                        No services selected
                      </Typography>
                    )}
                </Box>
              </Box>

              <Box sx={{ mt: "auto" }}>
                <Box sx={{ my: 2 }}>
                  <Typography
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: "bold",
                      fontSize: "12px",
                    }}
                  >
                    <span>Total</span>
                    <span>USD ${total}.00</span>
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  disabled={
                    !selectedServices.videoDownload &&
                    !selectedServices.memoryBook
                  }
                  onClick={handleCheckout}
                  sx={{
                    textTransform: "none",
                    backgroundColor: reduxTheme.primary_colors[400],
                    width: "100%",
                    "&.Mui-disabled": {
                      backgroundColor: "grey.300",
                      color: "grey.500",
                    },
                  }}
                >
                  Checkout
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: { xs: 4, md: 6 } }}>
        {[1, 2, 3].map((i) => (
          <Grid size={{ xs: 12, sm: 4 }} key={i}>
            <Card
              elevation={0}
              sx={{ borderRadius: 2, aspectRatio: "1 / 1" }}
            />
          </Grid>
        ))}
      </Grid>

      <CheckoutModal
        open={checkoutOpen}
        onClose={handleCloseCheckout}
        selectedServices={selectedServices}
        total={total}
      />
    </Box>
  );
}

export default AdminTab;
